<!-- src/components/map/ItemDetailPanel.vue -->
<template>
  <div class="filter-detail" :style="positionStyle">
    <div class="detail-head">
      <h3>{{ itemTitle }}</h3>
      <button class="close" type="button" @click="$emit('close')" aria-label="Close panel">✕</button>
    </div>

    <div class="detail-body" v-if="item">
      <div class="preview" role="figure" aria-label="Preview image">
        <div class="preview-wrap" @click="onMainImageClick" role="button" tabindex="0" @keydown.enter="onMainImageClick">
          <img
            v-if="hasImages"
            :src="mainImage"
            class="main-img"
            :alt="mainImageAlt"
            @error="onImageError"
          />
        </div>

        <div v-if="!hasImages" class="no-image-caption">Изображение отсутствует</div>
      </div>

      <!-- Поля -->
      <div class="fields">
        <div class="field">
          <span class="lbl">Бассейн</span>
          <span class="val">{{ item.basin || item.__raw?.basin || '—' }}</span>
        </div>

        <div class="field">
          <span class="lbl">Возраст</span>
          <span class="val">{{ item.department || item.__raw?.department || '—' }}</span>
        </div>

        <div class="field">
          <span class="lbl">Свита / горизонт</span>
          <span class="val">{{ item.suite_horizon || item.__raw?.suite_horizon || '—' }}</span>
        </div>

        <div class="field">
          <span class="lbl">Разрешение, µm</span>
          <span class="val">{{ formatResolution(item.resolution || item.__raw?.resolution) }}</span>
        </div>

        <div class="field">
          <span class="lbl">Краткое описание</span>
          <span class="val">{{ item.short_description || item.__raw?.short_description || '—' }}</span>
        </div>
      </div>

      <div class="actions">
        <button class="btn-view" @click="goToItem">Перейти к образцу</button>
      </div>
    </div>

    <!-- TELEPORTED modal (как раньше) -->
    <teleport to="body">
      <transition name="fade">
        <div
          v-if="isModalOpen"
          class="modal"
          @click.self="closeModal"
          role="dialog"
          aria-modal="true"
        >
          <button class="modal-close" @click="closeModal" aria-label="Close modal">✕</button>

          <div v-if="!hasImages" class="modal-noimage">
            <div class="noimage-box">
              <div class="noimage-ico">🖼️</div>
              <div class="noimage-text">Изображение отсутствует</div>
              <div class="noimage-sub">Нет доступных превью для этого образца.</div>
            </div>
          </div>

          <template v-else>
            <div class="modal-inner" @click.stop>
              <img :src="currentModalImage" class="modal-img" alt="Full size" @error="onModalImageError" />
            </div>
          </template>
        </div>
      </transition>
    </teleport>
  </div>
</template>

<script>
export default {
  name: 'ItemDetailPanel',
  props: {
    item: { type: Object, default: null },
    position: { type: Object, default: null }
  },
  emits: ['close'],
  data() {
    return {
      isModalOpen: false,
      modalIndex: 0,
      modalImageError: false,

      // кэш: ключ -> found single URL (string) или null
      resolvedImageByKey: {},

      // допустимые расширения (перебираем в этом порядке)
      _previewExts: ['webp','bmp','png','jpg','jpeg','tif','tiff',]
    }
  },
  computed: {
    itemTitle() {
      if (!this.item) return 'Образец'
      return this.item.renamed_id || this.item.__raw?.renamed_id || this.item.internal_id || this.item.id || 'Образец'
    },

    // modalImages — либо [] либо [singleUrl]
    modalImages() {
      const key = this._itemCacheKey(this.item)
      const v = this.resolvedImageByKey[key]
      return v ? [v] : []
    },

    hasImages() {
      return Array.isArray(this.modalImages) && this.modalImages.length > 0 && !this.modalImageError
    },

    mainImage() {
      if (this.hasImages) return this.modalImages[0]
      return '/img/placeholder-320.png'
    },

    mainImageAlt() {
      return this.hasImages ? 'Preview' : 'Placeholder - no image'
    },

    currentModalImage() {
      return this.mainImage
    },

    positionStyle() {
      const bottomFallback = `calc(var(--map-footer-space, 88px) + 12px)`
      if (!this.position) {
        // --fd-top можно переопределить глобально или через props при необходимости
        return { position: 'absolute', right: '24px', top: 'var(--fd-top)', '--fd-top': '120px', zIndex: 1250, width: '540px', overflow: 'hidden' }
      }
      // если передана position — сохраняем её, но добавляем переменную --fd-top и делаем top:var(--fd-top)
      const style = Object.assign({}, this.position)
      const explicitTop = this.position.top !== undefined ? this.position.top : '120px'
      style['--fd-top'] = String(explicitTop)
      style.top = 'var(--fd-top)'
      style.position = style.position || 'absolute'
      style.zIndex = style.zIndex || 1250
      // не добавляем bottom — CSS max-height будет ограничивать высоту панели
      style.overflow = style.overflow || 'hidden'
      return style
    }
  },
  watch: {
    item: {
      immediate: true,
      handler(newV) {
        if (newV) this.resolvePreviewForItem(newV).catch((e)=>{ console.warn('resolvePreviewForItem error', e) })
      }
    }
  },
  methods: {
    _itemCacheKey(it) {
      if (!it) return 'no-item'
      const raw = it.__raw || it
      const cand = it.renamed_id || it.internal_id || raw.internal_id || raw.renamed_id || raw.id || it.id
      return String(cand || Math.random().toString(36).slice(2))
    },

    /**
     * Попытаться определить превью для item.
     * Политика:
     * 1) использовать preview_images / image_url, если есть
     * 2) иначе пробуем /previews/{fadr}/{idKey}_1.{ext} (перебор ext)
     *    — пробуем сначала с encodeURIComponent, затем с "сырым" значением (на всякий случай)
     */
    async resolvePreviewForItem(it) {
      const key = this._itemCacheKey(it)
      // если уже есть кэш (включая null) — ничего не делаем
      if (this.resolvedImageByKey.hasOwnProperty(key)) {
        // console.debug('[ItemDetailPanel] cache hit for', key, this.resolvedImageByKey[key])
        return
      }

      // 1) быстрый путь: явно указанные превью в данных
      const extract = arr => (Array.isArray(arr) ? arr.map(p => (typeof p === 'string' ? p : (p && p.url) ? p.url : null)).filter(Boolean) : [])
      const pi = extract(it.preview_images)
      if (pi.length) { this.resolvedImageByKey[key] = pi[0]; return }
      const rawPi = extract(it.__raw && it.__raw.preview_images)
      if (rawPi.length) { this.resolvedImageByKey[key] = rawPi[0]; return }
      if (it.__raw && it.__raw.image_url) { this.resolvedImageByKey[key] = it.__raw.image_url; return }
      if (it.image_url) { this.resolvedImageByKey[key] = it.image_url; return }

      // 2) стандартный путь: /previews/{fadr}/{idKey}_1.{ext}
      const raw = it.__raw || it
      const fadr = (it.fadr || raw.fadr || '').toString().trim()
      const idKey = (it.renamed_id || it.internal_id || raw.internal_id || it.id || raw.id || '').toString().trim()
      if (!fadr || !idKey) {
        this.resolvedImageByKey[key] = null
        return
      }

      // две стратегии формирования пути: закодированная и "сырая"
      const baseEncoded = `/previews/${encodeURIComponent(fadr)}`
      const baseRaw = `/previews/${fadr}`

      // helper for trying one candidate and logging result
      const tryCandidate = async (candidate) => {
        // короткий лог — удобно при отладке
        // console.debug('[ItemDetailPanel] trying candidate', candidate)
        const ok = await this._tryLoadImage(candidate)
        if (ok) {
          this.resolvedImageByKey[key] = candidate
          // console.debug('[ItemDetailPanel] found image for', key, candidate)
          return true
        }
        return false
      }

      // попробовать все расширения сначала для encoded base, затем для raw base
      for (const base of [baseEncoded, baseRaw]) {
        for (const ext of this._previewExts) {
          const candidate = `${base}/${encodeURIComponent(idKey)}_1.${ext}`
          const ok = await tryCandidate(candidate)
          if (ok) return
        }
        // также попробовать без encode для filename (на случай, если encodeURIComponent ломает шаблон)
        for (const ext of this._previewExts) {
          const candidate = `${base}/${idKey}_1.${ext}`
          const ok = await tryCandidate(candidate)
          if (ok) return
        }
      }

      // не нашли
      this.resolvedImageByKey[key] = null
      // console.debug('[ItemDetailPanel] no preview found for', key)
    },

    // загружает картинку через Image() и ждёт onload/onerror
    _tryLoadImage(url) {
      return new Promise((resolve) => {
        try {
          const img = new Image()
          let settled = false
          img.onload = () => { if (!settled) { settled = true; resolve(true) } }
          img.onerror = () => { if (!settled) { settled = true; resolve(false) } }
          // cache-bust, чтобы не получить закэшированный 404
          img.src = url + (url.includes('?') ? '&_=' : '?_=') + Date.now()
          // дополнительный таймаут на случай подвисания (например, CORS блокирует onerror/onload) — 3s
          setTimeout(() => { if (!settled) { settled = true; resolve(false) } }, 3000)
        } catch (e) {
          resolve(false)
        }
      })
    },

    goToItem() {
      const pk = this.item && (this.item.id || this.item.__raw?.id || this.item.internal_id || this.item.__raw?.internal_id)
      if (!pk) return
      // this.$router.push({ path: `/items/${pk}` }).catch(()=>{})

      const url = `${window.location.origin}/items/${encodeURIComponent(String(pk))}`
      window.open(url, '_blank', 'noopener')
    },

    onMainImageClick() {
      if (!this.hasImages) {
        this.openModalNoImage()
      } else {
        this.openModal(0)
      }
    },

    openModal(startIndex = 0) {
      if (!this.hasImages) return this.openModalNoImage()
      this.modalIndex = Math.min(Math.max(0, startIndex), this.modalImages.length - 1)
      this.isModalOpen = true
      this.modalImageError = false
      document.body.style.overflow = 'hidden'
      document.body.classList.add('modal-open')
      window.addEventListener('keydown', this.onKeydown)
    },

    openModalNoImage() {
      this.modalImageError = true
      this.isModalOpen = true
      document.body.style.overflow = 'hidden'
      document.body.classList.add('modal-open')
      window.addEventListener('keydown', this.onKeydown)
    },

    closeModal() {
      this.isModalOpen = false
      this.modalImageError = false
      document.body.style.overflow = ''
      document.body.classList.remove('modal-open')
      window.removeEventListener('keydown', this.onKeydown)
    },

    onKeydown(e) {
      if (!this.isModalOpen) return
      if (e.key === 'Escape') {
        this.closeModal()
      }
    },

    onImageError() {
      this.modalImageError = true
    },

    onModalImageError() {
      this.modalImageError = true
    },

    formatResolution(v) {
      if (v === null || v === undefined || v === '') return '—'
      return v
    }
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.onKeydown)
    document.body.style.overflow = ''
    document.body.classList.remove('modal-open')
  }
}
</script>

<style scoped>
.filter-detail {
  width: 540px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.12);
  padding: 12px;
  z-index: 1250;
  color: #222;

  display: flex;
  flex-direction: column;
  box-sizing: border-box;

  /* max-height теперь зависит от --fd-top (top панели) и --map-footer-space */
  max-height: calc(100vh - var(--fd-top, 120px) - (var(--map-footer-space, 120px) + 12px));
  overflow: hidden;
}

/* header и кнопка закрыть */
.detail-head { display:flex; align-items:center; justify-content:space-between; gap:8px; flex: 0 0 auto; }
.detail-head h3 { margin:0; font-size:20px; color:#222; }
.close { background:transparent; border:none; cursor:pointer; font-size:18px }
.close:hover { color:#000; transform: translateY(-1px); }

.detail-body {
  flex: 1 1 auto;
  min-height: 0; /* важно для корректной работы flex + overflow */
  overflow: auto;
  padding-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.preview { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:8px }
.preview-wrap {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  /* ограничиваем высоту превью-блока — чтобы картинка не выходила за панель */
  max-height: min(32vh, 400px);        /* можно подправить по вкусу */
  overflow: hidden;         /* скрыть лишнее, если что-то пойдёт не так */
  box-sizing: border-box;
}

/* Большая картинка в превью: масштабируем по высоте, не растягиваем по ширине */
.main-img {
  /* вместо width:100% / object-fit:cover */
  max-width: 100%;
  width: auto;
  max-height: min(32vh, 400px);       /* совпадает с preview-wrap */
  height: auto;
  object-fit: contain;     /* сохраняет соотношение сторон */
  border-radius: 12px;
  cursor: pointer;
  background: #eee;
  display: block;
}
.placeholder-large { width:100%; height:360px; display:flex; align-items:center; justify-content:center; border-radius:12px; background:#f2f2f2; }
.placeholder-img { width:320px; height:auto; opacity:0.9; }
.no-image-caption { color: #666; font-weight:700; margin-top:8px }

.fields{ display:flex; flex-direction:column; gap:8px; padding-top:8px }
.field{ display:flex; gap:12px; align-items:center }
.lbl{ color:#888; min-width:160px }
.val{ color:#222; font-weight:700 }
.actions{ margin-top:12px; display:flex; justify-content:flex-end }
.btn-view{ background:var(--hp-button-color,#F6F6F6); border:none; padding:8px 12px; border-radius:8px; cursor:pointer; font-weight:700 }

/* --- МОДАЛ --- */
.fade-enter-active, .fade-leave-active { transition: opacity .18s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

.modal {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  display:flex;
  align-items:center;
  justify-content:center;
  z-index: 10050;
  padding:24px;
  box-sizing:border-box;
  pointer-events: auto;
}

.modal-inner {
  position: relative;
  max-width: calc(100% - 160px);
  /* ограничиваем высоту модалки до высоты окна браузера */
  max-height: calc(100vh - 160px);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  overflow: hidden;
}

/* Картинка внутри модалки: масштабируем по высоте (и по ширине если нужно) */
.modal-img {
  max-width: 100%;
  width: auto;
  max-height: calc(100vh - 200px); /* немного меньше чем modal-inner чтобы не упиралось */
  height: auto;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.6);
}

/* кнопка закрытия */
.modal-close {
  position: fixed;
  top: 18px;
  right: 18px;
  z-index: 10060;
  width: 44px;
  height: 44px;
  display:flex;
  align-items:center;
  justify-content:center;
  background:#fff;
  color:#222;
  border-radius:10px;
  border:none;
  box-shadow: 0 10px 30px rgba(0,0,0,0.45);
  cursor:pointer;
}
.modal-close:hover { transform: translateY(-2px); box-shadow: 0 14px 36px rgba(0,0,0,0.5); }

.modal-noimage { display:flex; align-items:center; justify-content:center; width:100%; height:100%; }
.noimage-box { text-align:center; color:#fff; padding:32px; border-radius:12px; background: rgba(0,0,0,0.25); }
.noimage-ico { font-size:48px; margin-bottom:12px } .noimage-text { font-size:20px; font-weight:700; margin-bottom:6px } .noimage-sub { opacity:0.85; font-size:14px }

@media (max-width:820px) {
  .filter-detail { width: 92%; right: 4%; left: 4%; }
  .main-img { max-height:320px; }
}
</style>
